/* Drop One Word ✦ — download dropdown.
 *
 * Compact picker for the four available export formats:
 *   1. PDF (A3 print-ready)
 *   2. PNG with walnut background ("as projected")
 *   3. PNG with transparent background (overlays / albums)
 *   4. SVG (vector, infinite zoom)
 *
 * Reused from both the Host Dashboard ("Save the wall ✦" CTA) and
 * the public OneWordPage ("Take it home ✦"). Same backend endpoint,
 * same layout, same colours — what you save is what the room saw.
 *
 * Note: this is a download trigger, not a preview. The browser
 * fetches the resource and opens its native save dialog. No client-
 * side rendering, no PDF.js bloat.
 */
import React, { useState } from 'react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FORMATS = [
  { id: 'pdf-a3',     label: 'PDF · A3 print',         url: (s) => `${API}/one-word/event/${s}/export?format=pdf&page_size=A3` },
  { id: 'pdf-a4',     label: 'PDF · A4 print',         url: (s) => `${API}/one-word/event/${s}/export?format=pdf&page_size=A4` },
  { id: 'png-walnut', label: 'PNG · walnut background', url: (s) => `${API}/one-word/event/${s}/export?format=png&background=walnut` },
  { id: 'png-trans',  label: 'PNG · transparent',      url: (s) => `${API}/one-word/event/${s}/export?format=png&background=transparent` },
  { id: 'svg',        label: 'SVG · vector',           url: (s) => `${API}/one-word/event/${s}/export?format=svg` },
];

export default function DropOneWordDownload({
  slug,
  label = 'Save the wall ✦',
  testId = 'dow-download',
  variant = 'gold', // 'gold' | 'quiet'
}) {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);

  const buttonStyle = variant === 'gold'
    ? {
        padding: '12px 22px', borderRadius: 999, border: 'none',
        cursor: 'pointer',
        background: 'linear-gradient(180deg, #c19a4b, #9a7836)',
        color: '#1a0f04', fontWeight: 700, letterSpacing: '0.04em',
        fontSize: 13,
      }
    : {
        padding: '10px 18px', borderRadius: 999,
        border: '1px solid rgba(193,154,75,0.5)',
        background: 'transparent', color: '#c19a4b',
        cursor: 'pointer', fontSize: 11.5, fontWeight: 700,
        letterSpacing: '0.18em', textTransform: 'uppercase',
      };

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid={`${testId}-trigger`}
        aria-expanded={open}
        style={buttonStyle}
      >{label}</button>

      {open && (
        <>
          {/* Click-away shield */}
          <div
            onClick={close}
            style={{
              position: 'fixed', inset: 0, zIndex: 9,
              background: 'transparent',
            }}
          />
          <div
            data-testid={`${testId}-menu`}
            style={{
              position: 'absolute', top: 'calc(100% + 8px)', right: 0,
              zIndex: 10, minWidth: 240,
              background: '#1a120a',
              border: '1px solid rgba(193,154,75,0.32)',
              borderRadius: 8,
              boxShadow: '0 18px 60px rgba(0,0,0,0.55)',
              padding: 6,
              fontFamily: "'Inter', system-ui, sans-serif",
            }}
          >
            <div style={{
              fontSize: 9.5, fontWeight: 700, letterSpacing: '0.28em',
              textTransform: 'uppercase', color: '#c19a4b',
              padding: '10px 14px 6px',
            }}>✦ Take it home</div>
            {FORMATS.map((f) => (
              <a
                key={f.id}
                href={f.url(slug)}
                onClick={close}
                data-testid={`${testId}-${f.id}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'block',
                  padding: '11px 14px',
                  fontSize: 13,
                  color: 'rgba(245,239,223,0.9)',
                  textDecoration: 'none',
                  borderRadius: 5,
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(193,154,75,0.10)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >{f.label}</a>
            ))}
            <div style={{
              fontSize: 10.5, color: 'rgba(245,239,223,0.42)',
              padding: '6px 14px 10px', fontStyle: 'italic',
              borderTop: '1px solid rgba(245,239,223,0.08)', marginTop: 4,
            }}>
              The wall, exactly as the room saw it.
            </div>
          </div>
        </>
      )}
    </div>
  );
}
